import { Container, Typography, Paper, Grid, Button, FormControl, InputLabel, Select, MenuItem, Dialog, DialogContent, Box, CircularProgress } from "@mui/material";
import { useAuth } from "../../../hooks/useAuth";
import { getInventoryLogsAPI, getMenuInventory, getStoreInfo, getUserTimeLogAPI } from "../../../hooks/apiHandler";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { ClockInReportPage } from "./ClockInReportPage";

import { handleViewPDF, handleDownloadPDF } from "../comp/handlePrint/handlePDFviewdownload";
import { TipReportPage } from "./tipReport";
import { EmployeeReportPage } from "./EmployeeReportPage";
import { SalesReportPage } from "./SalesReportPage";
import { OrderReportPage } from "./OrderReportPage";


// --- Placeholder/Mock Components for Other Reports ---
const DetailReportPage = () => <Typography variant="h4">Detail Report Content</Typography>;

// const OrderReportPage = () => <Typography variant="h4">Order Report Content</Typography>;
const ExpenseReportPage = () => <Typography variant="h4">Expense Report Content</Typography>;
const CashDropReportPage = () => <Typography variant="h4">Cash Drop Report Content</Typography>;

// This function now only fetches the raw data from the API.

export const CustomReports = ({ startDate, endDate, storeID, rawDataParent }) => {
    const { Logout } = useAuth();

    // State for the hidden PDF generation element
    const [pdfData, setPdfData] = useState({ report: '', rawData: [] });
    const printRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [storeInfo, setStoreInfo] = useState({
        name: '', email: '', phone: 0, gst: '', address: '', city: '', state: '', country: '', postCode: '', storeTimes: {}
    });

    useMemo(() => {
        async function fetchStoreInfo() {
            var response = await getStoreInfo({ storeID: storeID }, Logout);
            if (response) {
                setStoreInfo(response.data);
            }
        }
        fetchStoreInfo();
    }, [storeID, Logout]);

    const reportComponents = {
        'clock-in-report': <ClockInReportPage rawData={null} />,
        'tip-report': <TipReportPage rawData={null} />,
        'ep-report': <EmployeeReportPage rawData={null} />,
        'dep-report': <DetailReportPage />,
        'sales-report': <SalesReportPage rawData={null} />,
        'order-report': <OrderReportPage rawData={null} />,
        'exp-report': <ExpenseReportPage />,
        'cd-report': <CashDropReportPage />,
    };


    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState("");

    // extract unique employee names from your rawData
    useEffect(() => {
        const sourceData = pdfData?.rawData?.length ? pdfData.rawData : rawDataParent || [];
        if (sourceData && sourceData.length > 0) {
            const employees = new Set();
            sourceData.forEach(day => {
                const allOrders = [
                    ...(day.completedOrders || []),
                    ...(day.cancelOrders || [])
                ];
                allOrders.forEach(order => {
                    const name = order.orders?.[0]?.cName;
                    if (name) employees.add(name);
                });
            });
            setEmployeeList([...employees]);
        }
    }, [pdfData, rawDataParent]);


    const handleEmployeeReport = async (action) => {
        if (!selectedEmployee) {
            alert("Please select an employee first.");
            return;
        }

        // Filter rawData for the selected employee
        const filteredData = (pdfData?.rawData?.length ? pdfData.rawData : rawDataParent || []).map(day => ({
            completedOrders: (day.completedOrders || []).filter(order =>
                order.orders?.[0]?.cName === selectedEmployee
            ),
            cancelOrders: (day.cancelOrders || []).filter(order =>
                order.orders?.[0]?.cName === selectedEmployee
            ),
        }));

        // Save filtered data in sessionStorage (for new tab use)
        sessionStorage.setItem("employeeReportData", JSON.stringify(filteredData));

        const reportTitle = `${selectedEmployee} - Employee Report`;

        if (action === "view") {
            // ✅ Use the same hidden div approach
            setPdfData({ report: 'ep-report', rawData: filteredData });
            await new Promise(resolve => setTimeout(resolve, 150)); // wait for React to render hidden div

            await handleViewPDF(printRef, storeID, startDate, endDate, reportTitle, storeInfo);

            // Clear hidden div after use
            setPdfData({ report: '', rawData: [] });
        } else if (action === "open") {
            // Open EmployeeReportPage in a new tab
            window.open("/epr", "_blank");
        } else if (action === "download") {
            // Generate and download PDF
            setPdfData({ report: 'ep-report', rawData: filteredData });
            await new Promise(resolve => setTimeout(resolve, 150));

            await handleDownloadPDF(printRef, storeID, startDate, endDate, reportTitle, storeInfo);

            setPdfData({ report: '', rawData: [] });
        }
    };

    // This is the key for sessionStorage
    const storageKey = "inventoryLogReportData";
    

    const generateAndOpenReport = async () => {
        setIsLoading(true);
        console.log("Starting report generation...");

        try {
            // 1. Define all log types to fetch
            const logTypes = [
                'DELIVERY',
                'COUNT_ADJUSTMENT',
                'SPOILAGE',
                'RECIPE_CONSUMPTION',
                'INTERNAL_USE'
            ];

            // 2. Create all API call promises
            const itemPromise = getMenuInventory({ storeID, startDate, endDate, }, Logout);
            const logPromises = logTypes.map(logType =>
                getInventoryLogsAPI({ storeID, startDate, endDate, currentLogType: logType }, Logout)
            );

            // 3. Run all calls in parallel
            console.log("Fetching item data and all logs...");
            const [itemResponse, ...logResponses] = await Promise.all([itemPromise, ...logPromises]);

            // 4. Process all responses
            const items = itemResponse.data || [];

            const logs = {};
            logResponses.forEach((response, index) => {
                const logType = logTypes[index];
                logs[logType] = response.data || [];
            });

            // 5. Bundle the data into a single payload
            const reportPayload = {
                items,
                logs,
                generatedAt: new Date().toISOString() // Add a timestamp
            };
            console.log("Data fetched, payload created:", reportPayload);


            // 6. Save the payload to sessionStorage
            sessionStorage.setItem(storageKey, JSON.stringify(reportPayload));
            console.log("Data saved to sessionStorage.");

            // 7. Open the report page in a new tab
            window.open('/inventory-report-logs', '_blank');

        } catch (err) {
            console.error("Failed to generate inventory log report:", err);
            // Optionally show an error to the user (e.g., with a snackbar)
        } finally {
            setIsLoading(false);
        }
    };
    const storageKey2 = "inventoryReportData";
    const openInventoryReport = async () => {
        setIsLoading(true);
        console.log("Starting stock report generation...");

        try {
            // 1. Fetch the inventory data
            const response = await getMenuInventory({ storeID, startDate, endDate, }, Logout);

            // 2. Bundle the data in the format your report page expects: { rawData: [...] }
            const reportPayload = {
                rawData: response.data || []
            };
            console.log("Stock data fetched, payload created:", reportPayload);

            // 3. Save the payload to sessionStorage
            sessionStorage.setItem(storageKey2, JSON.stringify(reportPayload));
            console.log("Data saved to sessionStorage.");

            // 4. Open the report page in a new tab (update route if needed)
            window.open('/inventory-report', '_blank');

        } catch (err) {
            console.error("Failed to generate inventory stock report:", err);
            // Optionally show an error to the user
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 3, my: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Employee
                </Typography>

                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222"> Clock-in Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        {/* <Button sx={{ mx: '10px' }} variant="contained"
                            onClick={() => handleReportAction('clock-in-report', 'view')}
                        >View</Button> */}
                        <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            onClick={async () => {
                                try {
                                    // Fetch the latest data
                                    const rawData = await getUserTimeLogAPI({ storeID, startDate, endDate }, Logout);

                                    // Prepare payload
                                    const reportPayload = {
                                        storeID,
                                        startDate,
                                        endDate,
                                        rawData: rawData?.data || [],
                                    };

                                    // Store in sessionStorage
                                    sessionStorage.setItem("clockInReportData", JSON.stringify(reportPayload));

                                    // Open in a new tab
                                    window.open("/clock-in", "_blank");
                                } catch (err) {
                                    console.error("Failed to fetch report data:", err);
                                }
                            }}
                        >
                            View/Download
                        </Button>
                        {/* <Button sx={{ mx: '10px' }} variant="outlined"
                            onClick={() => handleReportAction('clock-in-report', 'download')}
                        >Download</Button> */}
                    </Grid>
                </Grid>

                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222"> Tip Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>

                        {/* <Button sx={{ mx: '10px' }} variant="contained" onClick={() => handleReportAction('tip-report', 'view')}>View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined" onClick={() => handleReportAction('tip-report', 'download')}>Download</Button> */}
                        <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            onClick={async () => {
                                try {
                                    // Fetch or use existing data
                                    const data = pdfData?.rawData?.length ? pdfData.rawData : rawDataParent || [];

                                    // Prepare payload with all details
                                    const reportPayload = {
                                        storeID,
                                        startDate,
                                        endDate,
                                        rawData: data,
                                    };

                                    // Store in sessionStorage
                                    sessionStorage.setItem("tipReportData", JSON.stringify(reportPayload));

                                    // Open the tip report page in a new tab
                                    window.open("/tip", "_blank");
                                } catch (err) {
                                    console.error("Failed to open tip report:", err);
                                }
                            }}
                        >
                            View/Download
                        </Button>
                    </Grid>
                </Grid>


                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222"> Employee Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        {/* <Button
                            sx={{ mx: '10px' }}
                            variant="contained"
                            onClick={() => handleReportAction('ep-report', 'view')}
                        >
                            View
                        </Button> */}

                        <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            onClick={async () => {
                                try {
                                    // Use parent data (already passed from backend)
                                    const data = pdfData?.rawData?.length ? pdfData.rawData : rawDataParent || [];

                                    // Prepare data payload for new tab
                                    const reportPayload = {
                                        storeID,
                                        startDate,
                                        endDate,
                                        rawData: data,
                                    };

                                    // Save in sessionStorage
                                    sessionStorage.setItem("employeeReportData", JSON.stringify(reportPayload));

                                    // Open Employee Report page in new tab
                                    window.open("/epr", "_blank");
                                } catch (err) {
                                    console.error("Failed to open Employee Report:", err);
                                }
                            }}
                        >
                            View/Download
                        </Button>

                        {/* <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            onClick={() => handleReportAction('ep-report', 'download')}
                        >
                            Download
                        </Button> */}
                    </Grid>
                </Grid>

                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4} md={4}>
                                <Typography variant="p" color="#222">Detail Report</Typography>
                            </Grid>
                            <Grid item xs={6} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="report-user-list-label">Select User</InputLabel>
                                    <Select
                                        labelId="report-user-list-label"
                                        value={selectedEmployee}
                                        label="Select User"
                                        onChange={(e) => setSelectedEmployee(e.target.value)}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {employeeList.map((emp, index) => (
                                            <MenuItem key={index} value={emp}>{emp}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        {/* <Button
                            sx={{ mx: '10px' }}
                            variant="contained"
                            onClick={() => handleEmployeeReport('view')}
                        >
                            View
                        </Button> */}

                        <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            onClick={() => handleEmployeeReport('open')}
                        >
                            View/Download
                        </Button>

                        {/* <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            onClick={() => handleEmployeeReport('download')}
                        >
                            Download
                        </Button> */}
                    </Grid>
                </Grid>
            </Paper>
            <Paper elevation={2} sx={{ p: 3, my: 3, backgroundColor: "#fff3e0" }}>
                {/* This all Folllowing report can be generated form rawData */}
                {/* /// Store Reports
                1. Sale Report
                2. Order Report
                3. Expense Report
                4. Cash Drop Report
                5. Paid Out Report 
                6. Tax Report
                7. Discount Report
                8. Refund Report
                9. Customer Report */}

                <Typography variant="h6" color="warning.main" gutterBottom>
                    Store
                </Typography>
                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222"> Sale Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        {/* <Button sx={{ mx: '10px' }} variant="contained" onClick={() => handleReportAction('sales-report', 'view')}>View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined" onClick={() => handleReportAction('sales-report', 'download')}>Download</Button> */}
                        <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            onClick={() => { // Changed from async ()
                                try {
                                    // Determine the source of rawData - prefer state if available
                                    const currentRawData = pdfData?.rawData?.length ? pdfData.rawData : rawDataParent || [];

                                    // Prepare data payload for new tab
                                    const reportPayload = {
                                        storeID,
                                        startDate,
                                        endDate,
                                        rawData: currentRawData, // Use the determined rawData
                                    };

                                    // Save in sessionStorage with the key "salesReportData"
                                    sessionStorage.setItem("salesReportData", JSON.stringify(reportPayload));

                                    // Open Sales Report page in new tab
                                    window.open("/sales-report", "_blank");
                                } catch (err) {
                                    console.error("Failed to open Sales Report:", err);
                                    // Optionally, show an error message to the user here
                                }
                            }}
                        >
                            View/Download
                        </Button>
                    </Grid>
                </Grid>

                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Order Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        {/* <Button sx={{ mx: '10px' }} variant="contained" onClick={() => handleReportAction('order-report', 'view')}>View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined" onClick={() => handleReportAction('order-report', 'download')}>Download</Button> */}
                        <Button
                            sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            onClick={() => {

                                const data = Array.isArray(rawDataParent) ? rawDataParent : [];
                                const reportPayload = { storeID, startDate, endDate, rawData: data };
                                sessionStorage.setItem("orderReportData", JSON.stringify(reportPayload));
                                window.open("/orders-report", "_blank");

                            }}
                        >
                            View/Download
                        </Button>
                    </Grid>
                </Grid>
                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Inventory Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button
                           sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            disabled={isLoading}
                            onClick={openInventoryReport}
                            
                        >
                           View/Download
                        </Button>
                    </Grid>
                </Grid>

                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Inventory Logs Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button
                           sx={{ mx: '10px' }}
                            variant="outlined"
                            color="primary"
                            disabled={isLoading}
                            onClick={generateAndOpenReport}
                          
                        >
                            View/Download
                        </Button>
                    </Grid>
                </Grid>

                {/* <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Expense Report</Typography>
                       
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button sx={{ mx: '10px' }} variant="contained" onClick={() => handleReportAction('exp-report', 'view')}>View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined" onClick={() => handleReportAction('exp-report', 'download')}>Download</Button>
                    </Grid>
                </Grid>
                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Cash Drop Report</Typography>
                       
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button sx={{ mx: '10px' }} variant="contained" onClick={() => handleReportAction('cd-report', 'view')}>View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined" onClick={() => handleReportAction('cd-report', 'download')}>Download</Button>
                    </Grid>
                </Grid> */}
            </Paper>
            {/* <Paper elevation={2} sx={{ p: 3, backgroundColor: "#f2f3e0" }}>
                <Typography variant="h6" color="warning.main" gutterBottom>
                    Inventory
                </Typography>
                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222"> Product Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button sx={{ mx: '10px' }} variant="contained">View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined">Download</Button>
                    </Grid>
                </Grid>

                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>

                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Stock Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button sx={{ mx: '10px' }} variant="contained">View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined">Download</Button>
                    </Grid>
                </Grid>
                {/* <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Order Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button sx={{ mx: '10px' }} variant="contained">View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined">Download</Button>
                    </Grid>
                </Grid>
                <Grid sx={{ my: '5px' }} alignItems={'center'} container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="p" color="#222">Expense Report</Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Button sx={{ mx: '10px' }} variant="contained">View</Button>
                        <Button sx={{ mx: '10px' }} variant="outlined">Download</Button>
                    </Grid>
                </Grid> 
            </Paper> */}
            {/* The Dialog for viewing the report */}
            {/* A single, simple loading dialog */}
            <Dialog
                open={isLoading}
                fullWidth
                maxWidth="sm"
            >
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Generating report...</Typography>
                    </Box>
                </DialogContent>
            </Dialog>

            <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
                {pdfData.report && React.cloneElement(reportComponents[pdfData.report], { ref: printRef, rawData: pdfData.rawData })}
            </div>
        </Container>
    );
};

